// import libraries
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { firebase as db } from '../../../services/firebase';

// Fetch a single user record by id (one-shot)
export async function fetchUserRecordById(userId) {
  if (!userId) throw new Error('fetchUserRecordById requires a userId');
  
  // Check local storage first
  const cacheKey = `userRecord_${userId}`;
  const cachedData = localStorage.getItem(cacheKey);
  
  if (cachedData) {
    try {
      const data = JSON.parse(cachedData);
      return data;
    } catch (e) {
      console.warn('[userRecord] Failed to parse cached data, fetching from Firebase', e);
    }
  }
  
  try {
    const ref = doc(db, 'users', userId);
    const snap = await getDoc(ref);
    
    if (!snap.exists()) {
      console.warn(`[userRecord] No document found at users/${userId}`);
      return null;
    }
    
    const data = { id: snap.id, ...snap.data() };
    // Cache the result
    localStorage.setItem(cacheKey, JSON.stringify(data));
    return data;
  } catch (error) {
    console.error(`[userRecord] Error fetching user record:`, error);
    throw error;
  }
}

// Subscribe to a user record; returns unsubscribe function
export function subscribeUserRecordById(userId, onData, onError) {
  if (!userId) throw new Error('subscribeUserRecordById requires a userId');
  
  // Check local storage first
  const cacheKey = `userRecord_${userId}`;
  const cachedData = localStorage.getItem(cacheKey);
  
  if (cachedData) {
    try {
      const data = JSON.parse(cachedData);
      onData?.(data);
    } catch (e) {
      console.warn('[userRecord] Failed to parse cached subscription data', e);
    }
  }
  
  try {
    const ref = doc(db, 'users', userId);
    
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          console.warn(`[userRecord] No document found in subscription at users/${userId}`);
          onData?.(null);
          return;
        }
        
        const data = { id: snap.id, ...snap.data() };
        // Update cache on each update
        localStorage.setItem(cacheKey, JSON.stringify(data));
        onData?.(data);
      },
      (err) => {
        console.error(`[userRecord] Error in user record subscription:`, err);
        onError?.(err);
      }
    );
    
    return unsub;
  } catch (error) {
    console.error(`[userRecord] Error setting up subscription:`, error);
    throw error;
  }
}
